/**
 * Fristen und Eskalation je Queue (Spec 8.3 / 8.4).
 *
 * **Feste Struktur, kein Regel-Builder** — genau eine Regel je Queue, und die
 * steht hier vollstaendig auf dem Bildschirm. Wer mehr braucht, braucht keine
 * groessere Oberflaeche, sondern eine andere Spec.
 */
import {
  normalizeQueueSettings,
  TICKET_PRIORITIES,
  type Queue,
  type QueueSettings,
  type Team,
  type TenantMemberDetail,
  type TicketPriority,
} from '@tickethub/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

type QueueWorkflowSettingsProps = {
  queue: Queue;
  members: TenantMemberDetail[];
  teams: Team[];
  disabled: boolean;
  onSave: (settings: QueueSettings) => Promise<void>;
};

/** Leeres Feld = „keine automatische Frist" (Spec 8.3). */
function toHours(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function QueueWorkflowSettings({
  queue,
  members,
  teams,
  disabled,
  onSave,
}: QueueWorkflowSettingsProps) {
  const { t } = useTranslation();
  const current = normalizeQueueSettings(queue.settings);

  const [due, setDue] = useState<Record<TicketPriority, string>>({
    normal: current.due_hours?.normal?.toString() ?? '',
    important: current.due_hours?.important?.toString() ?? '',
    urgent: current.due_hours?.urgent?.toString() ?? '',
  });
  const [enabled, setEnabled] = useState(current.escalation?.enabled ?? false);
  const [hours, setHours] = useState(String(current.escalation?.no_reply_hours ?? 24));
  const [raiseTo, setRaiseTo] = useState<TicketPriority>(
    current.escalation?.raise_priority_to ?? 'important',
  );
  const [notifyMember, setNotifyMember] = useState(current.escalation?.notify_member_id ?? '');
  const [notifyTeam, setNotifyTeam] = useState(current.escalation?.notify_team_id ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const dueHours =
        due.normal === '' && due.important === '' && due.urgent === ''
          ? null
          : {
              normal: toHours(due.normal),
              important: toHours(due.important),
              urgent: toHours(due.urgent),
            };

      await onSave({
        due_hours: dueHours,
        escalation: enabled
          ? {
              enabled: true,
              no_reply_hours: toHours(hours) ?? 24,
              raise_priority_to: raiseTo,
              notify_member_id: notifyMember === '' ? null : notifyMember,
              notify_team_id: notifyTeam === '' ? null : notifyTeam,
            }
          : null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="space-y-4 rounded-lg border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="space-y-2">
        <h3 className="text-sm font-medium">{t('queue.due_title')}</h3>
        <p className="text-xs text-muted-foreground">{t('queue.due_intro')}</p>
        <div className="flex flex-wrap gap-3">
          {TICKET_PRIORITIES.map((priority) => (
            <div key={priority} className="space-y-1">
              <Label htmlFor={`due-${queue.id}-${priority}`}>
                {t(`ticket.priority_${priority}`)}
              </Label>
              <Input
                id={`due-${queue.id}-${priority}`}
                type="number"
                min={1}
                className="w-28"
                disabled={disabled}
                value={due[priority]}
                placeholder={t('queue.due_none')}
                onChange={(event) =>
                  setDue((value) => ({ ...value, [priority]: event.target.value }))
                }
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">{t('queue.escalation_title')}</h3>
        <p className="text-xs text-muted-foreground">{t('queue.escalation_intro')}</p>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={disabled}
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          {t('queue.escalation_enabled')}
        </label>

        {enabled ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`esc-hours-${queue.id}`}>{t('queue.escalation_hours')}</Label>
              <Input
                id={`esc-hours-${queue.id}`}
                type="number"
                min={1}
                disabled={disabled}
                value={hours}
                onChange={(event) => setHours(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`esc-prio-${queue.id}`}>{t('queue.escalation_raise_to')}</Label>
              <Select
                id={`esc-prio-${queue.id}`}
                disabled={disabled}
                value={raiseTo}
                onChange={(event) => setRaiseTo(event.target.value as TicketPriority)}
              >
                {TICKET_PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {t(`ticket.priority_${priority}`)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`esc-member-${queue.id}`}>{t('queue.escalation_notify_member')}</Label>
              <Select
                id={`esc-member-${queue.id}`}
                disabled={disabled}
                value={notifyMember}
                onChange={(event) => setNotifyMember(event.target.value)}
              >
                <option value="">{t('common.none')}</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`esc-team-${queue.id}`}>{t('queue.escalation_notify_team')}</Label>
              <Select
                id={`esc-team-${queue.id}`}
                disabled={disabled}
                value={notifyTeam}
                onChange={(event) => setNotifyTeam(event.target.value)}
              >
                <option value="">{t('common.none')}</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        ) : null}
      </div>

      <Button type="submit" size="sm" disabled={disabled || busy}>
        {busy ? t('common.saving') : t('common.save')}
      </Button>
    </form>
  );
}
